import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { map, prop } from 'ramda'
import { generatePath, useNavigate } from 'react-router-dom'
import { GridActionsCellItem } from '@mui/x-data-grid-pro'
import EditIcon from '@mui/icons-material/Edit'

import { NewButton } from '~/components/Buttons'
import PageTitle from '~/components/PageTitle'
import BooleanStatus from '~/components/BooleanStatus'
import Container, { Content, Header } from '~/components/Container'
import TextOverflow from '~/components/TextOverflow'
import { Table, TableHeader } from '~/components/Table'
import * as NAV from '~/constants/nav-titles'
import * as ROUTES from '~/constants/routes'
import { WAREHOUSE_TABS } from '~/constants/tabs'
import TableLink from '~/components/Link'

import More from './More'

const getDetailPath = guid => {
  const params = { guid, tab: WAREHOUSE_TABS.GENERAL }
  return generatePath(ROUTES.WAREHOUSE_DETAIL_PATH, params)
}

export const WAREHOUSE_COLUMNS = [
  {
    width: 250,
    headerName: 'Name',
    field: 'name',
    renderCell: ({ value, row }) => (
      <TableLink to={row.link}>
        <TextOverflow selfTooltip={true}>{value}</TextOverflow>
      </TableLink>
    )
  },
  {
    width: 150,
    headerName: 'Label',
    field: 'label'
  },
  {
    width: 200,
    headerName: 'City',
    field: 'city',
    ordering: true,
  },
  {
    width: 200,
    headerName: 'Email',
    field: 'email',
    renderCell: ({ value }) => (
      <TextOverflow selfTooltip={true}>{value}</TextOverflow>
    )
  },
  {
    width: 200,
    headerName: 'Phone',
    field: 'phone',
  },
  {
    width: 200,
    headerName: 'Primary contact',
    field: 'primaryContact',
    ordering: true,
  },
  {
    width: 200,
    headerName: 'Status',
    field: 'status',
    ordering: true,
    renderCell: ({ value }) => (
      <BooleanStatus value={value} />
    )
  }
]

const getRowsFromResults = map(item => {
  const id = prop('id', item)
  const guid = prop('guid', item)
  const name = prop('name', item)
  const label = prop('label', item)
  const city = prop('city', item)
  const email = prop('email', item)
  const phone = prop('phoneNumber', item)
  const firstName = prop('contactFirstName', item)
  const lastName = prop('contactLastName', item)
  const primaryContact = firstName + ' ' + lastName
  const status = prop('status', item)
  const link = getDetailPath(guid)

  return {
    id,
    guid,
    name,
    label,
    city,
    email,
    phone,
    primaryContact,
    status,
    link
  }
})

function WarehouseTable ({ list, filter, ordering, onListRefetch }) {
  const [tableRef, setTableRef] = useState(null)
  const navigate = useNavigate()
  const isLoading = prop('isLoading', list)
  const results = prop('results', list)
  const count = prop('count', list)
  const columns = prop('filteredColumns', ordering)

  const handleEdit = useCallback(guid => {
    const editPath = generatePath(ROUTES.WAREHOUSE_UPDATE_PATH, { guid })
    navigate(editPath)
  }, [navigate])

  const actionColumns = useMemo(() => [
    ...columns,
    {
      width: 50,
      resizable: false,
      field: 'actions',
      type: 'actions',
      getActions: ({ row }) => [
        <GridActionsCellItem
          label="Edit"
          onClick={() => handleEdit(row.guid)}
          showInMenu={true}
          icon={<EditIcon />}
        />
      ]
    }
  ], [columns, handleEdit])

  return (
    <Container>
      <Header>
        <PageTitle
          pageTitle={NAV.WAREHOUSES}
          parentTitle={NAV.WAREHOUSE}
          rightButton={(
            <NewButton path={ROUTES.WAREHOUSE_CREATE_PATH} />
          )}
        />
      </Header>

      <Content>
        <TableHeader
          filter={filter}
          filterOpen={filter.handleOpen}
          orderingOpen={ordering.handleOpen}
          ordering={ordering}
        >
          <More tableRef={tableRef} />
        </TableHeader>
        <Table
          getRowsFromResults={getRowsFromResults}
          results={results}
          count={count}
          primaryKey="guid"
          columns={actionColumns}
          checkboxSelection={false}
          isLoading={isLoading}
          pinnedColumns={['name']}
          ordering={ordering}
          onRefetch={onListRefetch}
          setTableRef={setTableRef}
        />
      </Content>
    </Container>
  )
}

WarehouseTable.propTypes = {
  filter: PropTypes.object.isRequired,
  ordering: PropTypes.object.isRequired,
  list: PropTypes.object.isRequired,
  onListRefetch: PropTypes.func.isRequired
}

export default WarehouseTable
